/**
 * Citation helper utilities for shared logic between citation components.
 */

import { toTitleCase } from './stringUtils.js';

/**
 * Extracts the first non-empty string value from a tag that can be a string or array.
 * @param {string|string[]|undefined|null} tagValue - Raw tag value
 * @returns {string|null} Trimmed string value or null if empty/missing
 */
export function extractTagValue(tagValue) {
    if (!tagValue) {
        return null;
    }

    const rawValue = Array.isArray(tagValue) ? tagValue[0] : tagValue;

    if (rawValue && rawValue.trim()) {
        return rawValue.trim();
    }

    return null;
}

/**
 * Gets the publication year from an issue object.
 * @param {Object} issue - Issue object containing publication_date
 * @returns {string} Publication year or empty string
 */
export function getPublicationYear(issue) {
    if (!Object.prototype.hasOwnProperty.call(issue, 'publication_date') || !issue.publication_date) {
        return '';
    }

    const dateStr = String(issue.publication_date).trim();
    const yearMatch = dateStr.match(/\d{4}/);

    if (yearMatch) {
        return yearMatch[0];
    }

    return dateStr;
}

/**
 * Gets the publisher name from tags.
 * @param {Object} tags - Issue tags containing publisher
 * @returns {string} Publisher name or empty string
 */
export function getPublisher(tags) {
    if (!Object.prototype.hasOwnProperty.call(tags, 'publisher') || !tags.publisher) {
        return '';
    }
    const publisher = Array.isArray(tags.publisher) ? tags.publisher.join(', ') : tags.publisher;
    return publisher.trim();
}

/**
 * Gets the page number from current location.
 * @param {number|string|Object} currentLocation - Current page/location
 * @param {boolean} isEpub - Whether this is an EPUB file
 * @param {boolean} includePageNumber - Whether to include page number
 * @returns {number|null} Page number or null if not applicable
 */
export function getPageNumber(currentLocation, isEpub, includePageNumber) {
    if (!includePageNumber || !currentLocation) {
        return null;
    }

    if (isEpub) {
        return null;
    }

    const pageNumber = parseInt(currentLocation, 10);
    if (!isNaN(pageNumber) && pageNumber > 0) {
        return pageNumber;
    }

    return null;
}

/**
 * Gets the title from issue or fallback.
 * @param {Object} issue - Issue object
 * @param {string} fallbackTitle - Fallback title
 * @returns {string} Title or empty string
 */
export function getTitle(issue, fallbackTitle) {
    if (Object.prototype.hasOwnProperty.call(issue, 'title') && issue.title && issue.title.length) {
        return issue.title;
    }
    return fallbackTitle || '';
}

/**
 * Gets the publication place from issue object.
 * @param {Object} issue - Issue object containing publicationPlace
 * @returns {string} Publication place or empty string
 */
export function getPublicationPlace(issue) {
    if (!issue || !issue.publicationPlace) {
        return '';
    }
    const place = String(issue.publicationPlace).trim();
    return place || '';
}

/**
 * Checks if the publication has edition data required for ABNT format.
 * @param {Object} tags - Issue tags object
 * @returns {boolean} True if publication edition data exists
 */
export function hasPublicationEdition(tags) {
    if (!tags) {
        return false;
    }

    return extractTagValue(tags['edition']) !== null;
}

/**
 * Filters valid contributors from tags.
 * @param {Object} tags - Issue tags containing contributors array
 * @returns {Array<{first_name: string, last_name: string}>} Array of valid contributor objects
 */
export function getValidContributors(tags) {
    if (!tags || !Array.isArray(tags.contributors) || tags.contributors.length === 0) {
        return [];
    }
    return tags.contributors.filter(contributor => contributor?.first_name?.trim() || contributor?.last_name?.trim());
}

/**
 * Gets the full name of a contributor in title case.
 * @param {{first_name: string, last_name: string}} contributor - Contributor object
 * @returns {string} Full name in title case (e.g., "Jane Austen")
 */
export function getContributorFullName(contributor) {
    const firstName = contributor.first_name?.trim() || '';
    const lastName = contributor.last_name?.trim() || '';
    if (!firstName) {
        return toTitleCase(lastName);
    }
    return toTitleCase(`${firstName} ${lastName}`);
}

/**
 * Gets the last name of a contributor in title case.
 * @param {{first_name: string, last_name: string}} contributor - Contributor object
 * @returns {string} Last name in title case (e.g., "Austen")
 */
export function getContributorLastName(contributor) {
    const lastName = contributor.last_name?.trim() || '';
    return toTitleCase(lastName);
}

/**
 * Formats contributor name as "Lastname, Firstname" (inverted) with title case formatting.
 * @param {{first_name: string, last_name: string}} contributor - Contributor object
 * @returns {string} Inverted name in title case (e.g., "Austen, Jane")
 */
export function formatContributorInvertedName(contributor) {
    const firstName = contributor.first_name?.trim() || '';
    const lastName = contributor.last_name?.trim() || '';
    const formattedLastName = toTitleCase(lastName);
    if (!firstName) {
        return formattedLastName;
    }
    return `${formattedLastName}, ${toTitleCase(firstName)}`;
}

/**
 * Checks if the publication has place data required for ABNT format.
 * @param {Object} issue - Issue object containing publicationPlace
 * @returns {boolean} True if publication place data exists
 */
export function hasPublicationPlace(issue) {
    if (!issue) {
        return false;
    }

    return extractTagValue(issue.publicationPlace) !== null;
}

/**
 * Checks if at least one contributor has a complete name (first_name and last_name).
 * @param {Object} tags - Issue tags object
 * @returns {boolean} True if at least one contributor has a complete name
 */
export function hasCompleteAuthor(tags) {
    if (!tags || !Array.isArray(tags.contributors) || tags.contributors.length === 0) {
        return false;
    }

    return tags.contributors.every(contributor => contributor?.first_name?.trim() && contributor?.last_name?.trim());
}

/**
 * Checks if at least one contributor has a last_name.
 * @param {Object} tags - Issue tags object
 * @returns {boolean} True if at least one contributor has a last_name
 */
export function hasAuthorLastName(tags) {
    if (!tags || !Array.isArray(tags.contributors) || tags.contributors.length === 0) {
        return false;
    }

    return tags.contributors.some(contributor => contributor?.last_name?.trim());
}

/**
 * Checks if the publication has title data.
 * @param {string|string[]|undefined|null} title - Title value from store or issue
 * @returns {boolean} True if title data exists
 */
export function hasTitle(title) {
    return extractTagValue(title) !== null;
}

/**
 * Checks if the publication has publisher data.
 * @param {Object} tags - Issue tags object
 * @returns {boolean} True if publisher data exists
 */
export function hasPublisher(tags) {
    if (!tags || !tags.publisher) {
        return false;
    }

    if (Array.isArray(tags.publisher)) {
        return tags.publisher.some(p => p && p.trim());
    }

    return extractTagValue(tags.publisher) !== null;
}

/**
 * Checks if the publication has a valid publication year (4 digits).
 * @param {Object} issue - Issue object
 * @returns {boolean} True if valid year exists
 */
export function hasPublicationYear(issue) {
    if (!issue || !issue.publication_date) {
        return false;
    }

    const dateStr = String(issue.publication_date).trim();
    const yearMatch = dateStr.match(/\d{4}/);

    return yearMatch !== null;
}

/**
 * Wraps citation with quoted text if text is provided.
 * @param {string} text - Selected text to cite
 * @param {string} citation - Generated citation string
 * @returns {string} Citation optionally prefixed with quoted text
 */
export function formatCitationWithText(text, citation) {
    if (text && text.trim()) {
        return `"${text}"

${citation}`;
    }
    return citation;
}
